import { useCallback, useState } from 'react'
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  cn,
} from '@duck/ui'
import { Database, Download, Loader2 } from 'lucide-react'
import { useTar } from '@/modules/i18n'
import { useCellOverlayContext } from './cellOverlayContext'
import type { BadgeState } from './useNotebookTables'
import type { TableBadgeInfo } from './useNotebookTables'

interface InjectDataPopoverProps {
  cellIndex: number
  tables: TableBadgeInfo[]
  badgeStates: Record<string, BadgeState>
  onInsert: (tableName: string, cellIndex: number) => void
  onDownload: (table: TableBadgeInfo) => Promise<boolean>
  triggerClassName?: string
}

export const InjectDataPopover = ({
  cellIndex,
  tables,
  badgeStates,
  onInsert,
  onDownload,
  triggerClassName,
}: InjectDataPopoverProps) => {
  const { t } = useTar()
  const { notifyOpenChange } = useCellOverlayContext()
  const [open, setOpen] = useState(false)

  const handleOpenChange = useCallback(
    (next: boolean) => {
      setOpen(next)
      notifyOpenChange(next)
    },
    [notifyOpenChange],
  )

  const handleClick = useCallback(
    async (table: TableBadgeInfo) => {
      const state = badgeStates[table.name]
      if (!state) return
      if (state.status === 'loaded') {
        onInsert(table.name, cellIndex)
        handleOpenChange(false)
        return
      }
      if (state.status === 'pending') {
        handleOpenChange(false)
        const ok = await onDownload(table)
        if (ok) onInsert(table.name, cellIndex)
      }
    },
    [badgeStates, onInsert, onDownload, cellIndex, handleOpenChange],
  )

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <Tooltip onOpenChange={notifyOpenChange}>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label={t('common.notebookCellInjectData')}
              className={cn(
                'inline-flex h-6 w-7 items-center justify-center rounded text-zinc-400 transition-colors',
                'hover:bg-black/5 hover:text-foreground dark:hover:bg-white/5 dark:hover:text-zinc-100',
                triggerClassName,
              )}
            >
              <Database className="h-3.5 w-3.5" />
            </button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent>{t('common.notebookCellInjectData')}</TooltipContent>
      </Tooltip>
      <PopoverContent className="w-56 p-1" align="end">
        {tables.length === 0 ? (
          <div className="p-2 text-xs text-muted-foreground">{t('common.notebookDataBarLabel')}</div>
        ) : (
          <ul className="flex flex-col gap-0.5">
            {tables.map((table) => {
              const state = badgeStates[table.name]
              const status = state?.status ?? 'pending'
              return (
                <li key={table.name}>
                  <button
                    type="button"
                    disabled={status === 'downloading'}
                    onClick={() => handleClick(table)}
                    className={cn(
                      'flex w-full items-center justify-between gap-2 rounded px-2 py-1.5 text-xs',
                      'hover:bg-accent hover:text-accent-foreground disabled:opacity-60 disabled:cursor-wait',
                    )}
                  >
                    <span className="font-mono truncate" dir="ltr">df_{table.name}</span>
                    {status === 'loaded' && <span className="text-[10px] text-muted-foreground">{table.rowCount.toLocaleString()}</span>}
                    {status === 'pending' && <Download className="h-3 w-3 shrink-0 text-muted-foreground" />}
                    {status === 'downloading' && <Loader2 className="h-3 w-3 shrink-0 animate-spin text-blue-400" />}
                  </button>
                </li>
              )
            })}
          </ul>
        )}
      </PopoverContent>
    </Popover>
  )
}

export default InjectDataPopover
